/**
 * FamilyHomeCard — IFT post-login "Family Home" hero block.
 *
 * Mobile-first dashboard element that:
 *   • Welcomes the user by name
 *   • Shows their primary family tree card (or "Start a tree" CTA)
 *   • Shows an "Upcoming" pill (next birthday/anniversary within 30 days)
 *   • Shows 5 big quick-action tiles: Add member · View tree · Add photo · Add story · Invite family
 *
 * Rendered at the top of /dashboard when the site is IndianFamilyTree.
 * Keeps the rest of the (SocialPulse-shared) dashboard intact below it.
 *
 * Data sources (all existing, no new backend):
 *   GET /api/family-trees?scope=mine
 *   GET /api/family-anniversaries/upcoming?window_days=30
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { http } from "@/lib/api";
import {
  TreePine, UserPlus, Camera, BookOpen, Share2,
  Cake, Heart, ArrowRight, Sparkles,
} from "lucide-react";

const P = {
  ink: "#431a04",
  inkSoft: "#8a5a2e",
  saffron: "#C2410C",
  saffronBg: "#FFF7ED",
  saffronBgDeep: "#FDE4C7",
  parchment: "#FFFBF4",
  line: "rgba(194,65,12,0.15)",
};

function fmtDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("en-IN", { day: "numeric", month: "short" });
  } catch { return ""; }
}

function daysUntil(iso) {
  if (!iso) return null;
  try {
    const t = new Date(iso).getTime();
    const now = Date.now();
    return Math.max(0, Math.round((t - now) / 86400000));
  } catch { return null; }
}

export default function FamilyHomeCard({ user }) {
  const [trees, setTrees] = useState(null);     // null = loading, [] = none
  const [upcoming, setUpcoming] = useState([]);

  useEffect(() => {
    let cancelled = false;
    http.get("/family-trees?scope=mine&limit=20")
      .then((r) => { if (!cancelled) setTrees(r.data?.items || []); })
      .catch(() => { if (!cancelled) setTrees([]); });
    http.get("/family-anniversaries/upcoming?window_days=30")
      .then((r) => { if (!cancelled) setUpcoming(r.data?.items || []); })
      .catch(() => { /* 403 for non-tree-members is fine */ });
    return () => { cancelled = true; };
  }, []);

  const primaryTree = useMemo(() => (trees && trees[0]) || null, [trees]);
  const nextEvent = upcoming[0];

  // --- Signed-in with no trees → gentle "Start your tree" CTA ---
  if (trees && trees.length === 0) {
    return (
      <div
        data-testid="family-home-empty"
        className="mb-8 rounded-3xl p-6 lg:p-8"
        style={{
          background: `linear-gradient(135deg, ${P.saffronBg} 0%, ${P.parchment} 60%)`,
          border: `1px solid ${P.line}`,
          boxShadow: "0 16px 40px -24px rgba(194,65,12,0.3)",
        }}
      >
        <div className="flex items-center gap-2 mb-2" style={{ color: P.saffron }}>
          <Sparkles className="w-4 h-4" />
          <span className="text-[11px] font-semibold tracking-widest uppercase">Welcome, {user?.name?.split(" ")[0] || "friend"}</span>
        </div>
        <h2 className="font-display text-2xl lg:text-3xl mb-2" style={{ color: P.ink }}>
          Your family's story is waiting.
        </h2>
        <p className="text-sm lg:text-base max-w-xl mb-5" style={{ color: P.inkSoft }}>
          Start by adding yourself. Then your parents, grandparents, and the people who shaped who you are.
        </p>
        <Link
          to="/family-trees/new"
          data-testid="family-home-start-tree"
          className="inline-flex items-center gap-2 rounded-full px-6 py-3 font-semibold text-sm transition-transform hover:scale-105"
          style={{ background: P.saffron, color: "#fff", boxShadow: "0 10px 20px -12px rgba(194,65,12,0.6)" }}
        >
          <TreePine className="w-4 h-4" />
          Start Your Family Tree
        </Link>
      </div>
    );
  }

  // Loading skeleton
  if (!trees) {
    return (
      <div
        className="mb-8 rounded-3xl p-6"
        style={{ background: P.saffronBg, border: `1px solid ${P.line}`, minHeight: 160 }}
        aria-busy="true"
      >
        <div className="h-5 w-32 rounded" style={{ background: "rgba(194,65,12,0.1)" }} />
        <div className="h-8 w-64 rounded mt-3" style={{ background: "rgba(194,65,12,0.1)" }} />
      </div>
    );
  }

  const treeSlug = primaryTree.slug || primaryTree.id;
  const quickActions = [
    { to: `/family/${treeSlug}?action=add-member`, Icon: UserPlus, label: "Add member",   testid: "fh-add-member" },
    { to: `/family/${treeSlug}`,                   Icon: TreePine, label: "View tree",    testid: "fh-view-tree" },
    { to: `/family/${treeSlug}?tab=albums`,        Icon: Camera,   label: "Add photo",    testid: "fh-add-photo" },
    { to: `/family/${treeSlug}?tab=archive`,       Icon: BookOpen, label: "Add story",    testid: "fh-add-story" },
    { to: `/family/${treeSlug}?tab=invite`,        Icon: Share2,   label: "Invite family",testid: "fh-invite" },
  ];

  return (
    <div
      data-testid="family-home-card"
      className="mb-8 rounded-3xl overflow-hidden"
      style={{
        background: `linear-gradient(135deg, ${P.saffronBg} 0%, ${P.parchment} 55%, #FFFFFF 100%)`,
        border: `1px solid ${P.line}`,
        boxShadow: "0 20px 50px -28px rgba(194,65,12,0.35)",
      }}
    >
      {/* Header strip — welcome + upcoming event pill */}
      <div className="px-5 lg:px-8 pt-5 lg:pt-7 pb-3">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <div className="flex items-center gap-2" style={{ color: P.saffron }}>
              <Sparkles className="w-4 h-4" />
              <span className="text-[11px] font-semibold tracking-widest uppercase">
                Welcome back, {user?.name?.split(" ")[0] || "friend"}
              </span>
            </div>
            <h2
              className="font-display text-xl lg:text-2xl mt-1 truncate"
              style={{ color: P.ink }}
              data-testid="family-home-heading"
            >
              The {primaryTree.name || "Family"}
            </h2>
            <p className="text-xs lg:text-sm mt-0.5" style={{ color: P.inkSoft }}>
              {primaryTree.member_count || 0} {(primaryTree.member_count || 0) === 1 ? "member" : "members"}
              {primaryTree.origin_location ? ` · rooted in ${primaryTree.origin_location}` : ""}
            </p>
          </div>

          {nextEvent && (
            <Link
              to={`/family/${treeSlug}`}
              data-testid="family-home-upcoming-pill"
              className="inline-flex items-center gap-2 rounded-full px-3.5 py-1.5 text-xs font-semibold transition-transform hover:scale-105"
              style={{
                background: "rgba(194,65,12,0.1)",
                color: P.saffron,
                border: `1px solid ${P.line}`,
              }}
            >
              {nextEvent.kind === "anniversary" ? <Heart className="w-3.5 h-3.5" /> : <Cake className="w-3.5 h-3.5" />}
              <span className="truncate max-w-[160px]">
                {nextEvent.member_name || nextEvent.name || "Upcoming"}
                {nextEvent.date ? ` · ${fmtDate(nextEvent.date)}` : ""}
              </span>
              <ArrowRight className="w-3 h-3" />
            </Link>
          )}
        </div>
      </div>

      {/* Quick-action tiles — 5 across on sm+, horizontal scroll on xs */}
      <div className="px-5 lg:px-8 pb-6 pt-2">
        <div className="grid grid-cols-5 gap-2 lg:gap-3">
          {quickActions.map(({ to, Icon, label, testid }) => (
            <Link
              key={testid}
              to={to}
              data-testid={testid}
              className="flex flex-col items-center justify-center gap-1.5 rounded-2xl py-3 lg:py-4 transition-transform hover:scale-[1.03]"
              style={{
                background: "rgba(255,255,255,0.7)",
                border: `1px solid ${P.line}`,
                color: P.ink,
                textAlign: "center",
                minHeight: 72,
              }}
            >
              <Icon className="w-5 h-5 lg:w-6 lg:h-6" style={{ color: P.saffron }} strokeWidth={1.8} />
              <span className="text-[10px] lg:text-xs font-medium leading-tight px-1">{label}</span>
            </Link>
          ))}
        </div>
      </div>

      {/* More trees — only if the user has multiple families */}
      {trees.length > 1 && (
        <div
          className="px-5 lg:px-8 py-3 flex items-center gap-2 overflow-x-auto"
          style={{ background: "rgba(194,65,12,0.04)", borderTop: `1px solid ${P.line}` }}
        >
          <span className="text-[11px] uppercase tracking-widest font-semibold shrink-0" style={{ color: P.inkSoft }}>
            Your families:
          </span>
          {trees.slice(1, 6).map((t) => (
            <Link
              key={t.id}
              to={`/family/${t.slug || t.id}`}
              data-testid={`family-home-other-${t.slug || t.id}`}
              className="shrink-0 rounded-full px-3 py-1 text-xs font-medium transition-colors"
              style={{
                background: "rgba(255,255,255,0.8)",
                border: `1px solid ${P.line}`,
                color: P.ink,
                textDecoration: "none",
              }}
            >
              {t.name}
            </Link>
          ))}
          {trees.length > 6 && (
            <Link
              to="/family-trees"
              data-testid="family-home-more-families"
              className="shrink-0 text-xs font-semibold"
              style={{ color: P.saffron }}
            >
              +{trees.length - 6} more
            </Link>
          )}
        </div>
      )}
    </div>
  );
}
